import { apiClient } from "./apiClient";
import { formatConceptCodeToLabel } from "./autoTaggingService";

export const TAGGING_RESULTS_AUTO_TAG_SEARCH_PATH =
  "/tagging-results/auto-tags/search";
export const TAGGING_RESULTS_CONDITIONAL_TAG_SEARCH_PATH =
  "/tagging-results/conditional-tags/search";
export const TAGGING_RESULTS_AUTO_TAG_DETAIL_PATH =
  "/tagging-results/auto-tags/detail";
export const TAGGING_RESULTS_CONDITIONAL_TAG_DETAIL_PATH =
  "/tagging-results/conditional-tags/detail";

export type TaggingResultsSearchCondition = {
  field: string;
  operator: string;
  value: string;
};

export type TaggingResultsSearchSort = {
  field: string;
  order: "asc" | "desc";
};

export type TaggingResultsSearchBody = {
  object_type: string;
  sub_type: string;
  filters: {
    conditions: TaggingResultsSearchCondition[];
  };
  sort: TaggingResultsSearchSort[];
  page: number;
  page_size: number;
};

export const TAGGING_RESULTS_DEFAULT_SORT: TaggingResultsSearchSort[] = [
  { field: "tagged_at", order: "desc" },
];

export const TAGGING_RESULTS_CONDITION_FIELD = {
  concept: "concept",
  conceptValueCode: "concept_value_code",
  objectId: "object_id",
} as const;

export type TaggingResultsSearchFilter = {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValue: string;
  objectId?: string;
  pageIndex: number;
  pageSize: number;
};

export type TaggingResultDisplayConcept = {
  name: string;
  displayName: string;
};

export type NormalizedTaggingResultItem = {
  id: string;
  objectType: string;
  objectTypeName: string;
  objectSubType: string;
  objectSubTypeName: string;
  conceptCodes: string[];
  title: string;
  version: string;
  taggingResults: string;
  tagsByConcept: Record<string, string>;
  /** Object detail URL from the search item `object_id_link` field. */
  link: string;
  verified: "verified" | "unverified" | null;
  status: "inProgress" | "complete" | null;
};

export type TaggingResultsSearchPage = {
  items: NormalizedTaggingResultItem[];
  displayConcepts: TaggingResultDisplayConcept[];
  total: number;
};

export type TaggingResultDetailRequest = {
  objectType: string;
  objectSubType: string;
  objectId: string;
  version: string;
};

export type TaggingResultDetailRequestBody = {
  object_type: string;
  sub_type: string;
  object_id: string;
  version: string;
};

export type TaggingResultReferenceDocument = {
  name: string;
  url: string;
};

export type TagOrigin = "System" | "Manual";

export type AutoTagDetailTag = {
  id: string;
  concept: string;
  tagValue: string;
  origin: TagOrigin;
  analysis: string;
  summary: string;
  recommendation: string;
};

export type AutoTagResultDetail = {
  objectId: string;
  title: string;
  version: string;
  references: TaggingResultReferenceDocument[];
  tags: AutoTagDetailTag[];
};

export type ConditionalTagDetailRule = {
  id: string;
  concept: string;
  tagValue: string;
  ruleId: string;
  matchedCondition: unknown;
};

export type ConditionalTagResultDetail = {
  objectId: string;
  title: string;
  version: string;
  status: "inProgress" | "complete" | null;
  references: TaggingResultReferenceDocument[];
  rules: ConditionalTagDetailRule[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(
  record: Record<string, unknown>,
  ...keys: string[]
): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function readNumber(
  record: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

const UNSAFE_OBJECT_LINK_PROTOCOL = /^(javascript|data|vbscript):/i;

export function toSafeObjectLink(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || UNSAFE_OBJECT_LINK_PROTOCOL.test(trimmed)) return "";
  return trimmed;
}

function displayNameForCode(code: string, explicit?: string): string {
  const named = explicit?.trim();
  if (named) return named;
  const mapped = formatConceptCodeToLabel(code);
  if (mapped) return mapped;
  return code.trim();
}

function formatTaggingResults(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        const record = asRecord(item);
        if (!record) return "";
        return readString(record, "name", "value", "label", "code");
      })
      .filter(Boolean)
      .join(", ");
  }
  const record = asRecord(value);
  if (!record) return "";
  return formatTaggingResults(
    record.tags ??
      record.values ??
      record.concepts ??
      record.items ??
      record.tagging_results,
  );
}

function readTagValueNames(value: unknown): string[] {
  if (typeof value === "string" && value.trim()) return [value.trim()];
  if (!Array.isArray(value)) {
    const record = asRecord(value);
    if (!record) return [];
    const name = readString(record, "name", "value", "label");
    return name ? [name] : [];
  }
  const names: string[] = [];
  for (const item of value) {
    if (typeof item === "string" && item.trim()) {
      names.push(item.trim());
      continue;
    }
    const record = asRecord(item);
    const name = record ? readString(record, "name", "value", "label") : "";
    if (name) names.push(name);
  }
  return names;
}

export function parseTagsByConcept(
  record: Record<string, unknown>,
): Record<string, string> {
  const raw = record.tags ?? record.tagging_results ?? record.taggingResults;
  if (!Array.isArray(raw)) return {};
  const tagsByConcept: Record<string, string> = {};
  for (const item of raw) {
    const tag = asRecord(item);
    if (!tag) continue;
    const conceptName = readString(
      tag,
      "concept_name",
      "conceptName",
      "name",
      "concept",
    );
    if (!conceptName) continue;
    const valueNames = readTagValueNames(tag.values ?? tag.value);
    if (valueNames.length === 0) continue;
    tagsByConcept[conceptName] = valueNames.join(", ");
  }
  return tagsByConcept;
}

function normalizeDisplayConcepts(
  raw: unknown,
): TaggingResultDisplayConcept[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const concepts: TaggingResultDisplayConcept[] = [];
  for (const item of raw) {
    const record = asRecord(item);
    if (!record) continue;
    const name = readString(record, "name", "code", "concept_name");
    if (!name || seen.has(name)) continue;
    seen.add(name);
    concepts.push({
      name,
      displayName:
        readString(record, "display_name", "displayName", "label") ||
        displayNameForCode(name),
    });
  }
  return concepts;
}

export function extractDisplayConcepts(
  payload: unknown,
): TaggingResultDisplayConcept[] {
  const record = asRecord(payload);
  if (!record) return [];
  const nested = asRecord(record.data);
  const candidates = [
    record.display_concepts,
    record.displayConcepts,
    nested?.display_concepts,
    nested?.displayConcepts,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return normalizeDisplayConcepts(candidate);
  }
  return [];
}

function readConceptCodes(record: Record<string, unknown>): string[] {
  const raw =
    record.concept_codes ??
    record.conceptCodes ??
    record.concepts ??
    record.dimensions;
  if (typeof raw === "string" && raw.trim()) return [raw.trim()];
  if (!Array.isArray(raw)) return [];
  const codes: string[] = [];
  for (const item of raw) {
    if (typeof item === "string" && item.trim()) {
      codes.push(item.trim());
      continue;
    }
    const nested = asRecord(item);
    const code = nested
      ? readString(nested, "code", "concept", "field", "name")
      : "";
    if (code) codes.push(code);
  }
  return codes;
}

export function normalizeVerifiedStatus(
  value: unknown,
): "verified" | "unverified" | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "verified" : "unverified";
  if (typeof value === "number") {
    if (value === 1) return "verified";
    if (value === 0) return "unverified";
    return null;
  }
  const normalized = String(value).trim().toLowerCase().replace(/[_\s-]/g, "");
  if (
    normalized === "verified" ||
    normalized === "true" ||
    normalized === "yes"
  ) {
    return "verified";
  }
  if (
    normalized === "unverified" ||
    normalized === "false" ||
    normalized === "no"
  ) {
    return "unverified";
  }
  return null;
}

export function normalizeConditionalStatus(
  value: unknown,
): "inProgress" | "complete" | null {
  if (value === null || value === undefined || value === "") return null;
  const normalized = String(value).trim().toLowerCase().replace(/[_\s-]/g, "");
  if (
    normalized === "complete" ||
    normalized === "completed" ||
    normalized === "success" ||
    normalized === "done"
  ) {
    return "complete";
  }
  if (
    normalized === "inprogress" ||
    normalized === "running" ||
    normalized === "pending" ||
    normalized === "processing"
  ) {
    return "inProgress";
  }
  return null;
}

export function normalizeTaggingResultItem(
  payload: unknown,
): NormalizedTaggingResultItem | null {
  const record = asRecord(payload);
  if (!record) return null;

  const nestedObject = asRecord(record.object);
  const objectType = readString(
    record,
    "object_type",
    "objectType",
    "target_type",
    "type",
  ) || (nestedObject ? readString(nestedObject, "type", "object_type") : "");
  const objectSubType = readString(
    record,
    "object_sub_type",
    "objectSubType",
    "sub_type",
    "subtype",
  ) || (nestedObject ? readString(nestedObject, "sub_type", "subtype") : "");
  const objectTypeName = displayNameForCode(
    objectType,
    readString(record, "object_type_name", "objectTypeName", "type_name"),
  );
  const objectSubTypeName = objectSubType
    ? displayNameForCode(
        objectSubType,
        readString(
          record,
          "object_sub_type_name",
          "objectSubTypeName",
          "sub_type_name",
        ),
      )
    : "";

  const objectId = readString(
    record,
    "object_id",
    "objectId",
    "id",
    "result_id",
    "resultId",
  );
  if (!objectId) return null;

  const version =
    readString(record, "version", "object_version", "objectVersion") ||
    (nestedObject
      ? readString(nestedObject, "version", "object_version", "objectVersion")
      : "");

  const tagsByConcept = parseTagsByConcept(record);
  const conceptCodes = readConceptCodes(record);
  const taggingResults =
    Object.values(tagsByConcept).filter(Boolean).join(", ") ||
    formatTaggingResults(
      record.tagging_results ??
        record.taggingResults ??
        record.tags ??
        record.results ??
        record.concepts,
    );

  return {
    id: objectId,
    objectType,
    objectTypeName: objectTypeName || objectType,
    objectSubType,
    objectSubTypeName,
    conceptCodes:
      conceptCodes.length > 0 ? conceptCodes : Object.keys(tagsByConcept),
    title: readString(record, "title", "name", "object_title"),
    version:
      readString(record, "version", "object_version", "objectVersion") ||
      (nestedObject
        ? readString(nestedObject, "version", "object_version", "objectVersion")
        : ""),
    taggingResults,
    tagsByConcept,
    link: toSafeObjectLink(
      readString(
        record,
        "object_id_link",
        "objectIdLink",
        "link",
        "url",
        "href",
        "object_link",
        "objectLink",
      ),
    ),
    verified: normalizeVerifiedStatus(
      record.verified ??
        record.is_verified ??
        record.isVerified ??
        record.verified_status,
    ),
    status: normalizeConditionalStatus(
      record.status ?? record.job_status ?? record.assessment_status,
    ),
  };
}

function extractItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const record = asRecord(payload);
  if (!record) return [];
  const nested = asRecord(record.data);
  const candidates = [
    record.data,
    record.items,
    record.results,
    record.rows,
    nested?.items,
    nested?.data,
    nested?.results,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

function extractTotal(payload: unknown, itemCount: number): number {
  const record = asRecord(payload);
  if (!record) return itemCount;
  const nested = asRecord(record.data);
  return (
    readNumber(record, "count", "total", "total_count") ??
    (nested ? readNumber(nested, "count", "total", "total_count") : undefined) ??
    itemCount
  );
}

function pushCondition(
  conditions: TaggingResultsSearchCondition[],
  field: string,
  operator: string,
  value: string,
): void {
  const trimmed = value.trim();
  if (!trimmed) return;
  conditions.push({ field, operator, value: trimmed });
}

export function buildTaggingResultsSearchBody(
  filter: TaggingResultsSearchFilter,
): TaggingResultsSearchBody {
  const conditions: TaggingResultsSearchCondition[] = [];
  pushCondition(
    conditions,
    TAGGING_RESULTS_CONDITION_FIELD.concept,
    "eq",
    filter.conceptCode,
  );
  pushCondition(
    conditions,
    TAGGING_RESULTS_CONDITION_FIELD.conceptValueCode,
    "eq",
    filter.conceptValue,
  );
  pushCondition(
    conditions,
    TAGGING_RESULTS_CONDITION_FIELD.objectId,
    "contains",
    filter.objectId ?? "",
  );

  return {
    object_type: filter.objectType.trim(),
    sub_type: filter.objectSubType.trim(),
    filters: { conditions },
    sort: TAGGING_RESULTS_DEFAULT_SORT,
    page: Math.max(0, filter.pageIndex) + 1,
    page_size: Math.max(1, filter.pageSize),
  };
}

function applySearchScope(
  item: NormalizedTaggingResultItem,
  filter: TaggingResultsSearchFilter,
): NormalizedTaggingResultItem {
  const objectType = item.objectType.trim() || filter.objectType.trim();
  const objectSubType = item.objectSubType.trim() || filter.objectSubType.trim();
  return {
    ...item,
    objectType,
    objectSubType,
    objectTypeName: item.objectTypeName.trim() || objectType,
    objectSubTypeName: item.objectSubTypeName.trim() || objectSubType,
  };
}

function parseSearchPage(
  payload: unknown,
  filter: TaggingResultsSearchFilter,
): TaggingResultsSearchPage {
  const items = extractItems(payload)
    .map((item) => normalizeTaggingResultItem(item))
    .filter((item): item is NormalizedTaggingResultItem => item !== null)
    .map((item) => applySearchScope(item, filter));
  return {
    items,
    displayConcepts: extractDisplayConcepts(payload),
    total: extractTotal(payload, items.length),
  };
}

async function searchTaggingResults(
  path: string,
  filter: TaggingResultsSearchFilter,
  fallbackError: string,
): Promise<TaggingResultsSearchPage> {
  const result = await apiClient.post<unknown>(
    path,
    buildTaggingResultsSearchBody(filter),
  );
  if (result.status !== "success") {
    throw new Error(result.message?.trim() || fallbackError);
  }
  return parseSearchPage(result, filter);
}

export async function searchAutoTagResults(
  filter: TaggingResultsSearchFilter,
): Promise<TaggingResultsSearchPage> {
  return searchTaggingResults(
    TAGGING_RESULTS_AUTO_TAG_SEARCH_PATH,
    filter,
    "Failed to load Classification Results.",
  );
}

export async function searchConditionalTagResults(
  filter: TaggingResultsSearchFilter,
): Promise<TaggingResultsSearchPage> {
  return searchTaggingResults(
    TAGGING_RESULTS_CONDITIONAL_TAG_SEARCH_PATH,
    filter,
    "Failed to load Conditional Classification Results.",
  );
}

export function buildTaggingResultsDetailBody(
  query: TaggingResultDetailRequest,
): TaggingResultDetailRequestBody {
  return {
    object_type: query.objectType.trim(),
    sub_type: query.objectSubType.trim(),
    object_id: query.objectId.trim(),
    version: query.version.trim(),
  };
}

function extractDetailPayload(
  payload: unknown,
): Record<string, unknown> | null {
  const record = asRecord(payload);
  if (!record) return null;
  const nested = asRecord(record.data);
  if (
    nested &&
    (nested.object != null ||
      nested.tags != null ||
      nested.matched_rules != null)
  ) {
    return nested;
  }
  if (
    record.object != null ||
    record.tags != null ||
    record.matched_rules != null
  ) {
    return record;
  }
  return null;
}

function parseReferenceDocuments(
  value: unknown,
): TaggingResultReferenceDocument[] {
  if (!Array.isArray(value)) return [];
  const documents: TaggingResultReferenceDocument[] = [];
  for (const item of value) {
    const record = asRecord(item);
    if (!record) continue;
    const name = readString(record, "name", "title", "label");
    const url = toSafeObjectLink(readString(record, "url", "href", "link"));
    if (!name || !url) continue;
    documents.push({ name, url });
  }
  return documents;
}

function normalizeTagOrigin(value: string): TagOrigin {
  return value.trim().toLowerCase() === "manual" ? "Manual" : "System";
}

function parseDetailObject(record: Record<string, unknown>): {
  objectId: string;
  title: string;
  version: string;
  references: TaggingResultReferenceDocument[];
  processingStatus: unknown;
} {
  const objectRecord = asRecord(record.object) ?? record;
  return {
    objectId: readString(objectRecord, "object_id", "objectId", "id"),
    title: readString(objectRecord, "title", "name"),
    version: readString(objectRecord, "version"),
    references: parseReferenceDocuments(
      objectRecord.reference_documents ?? objectRecord.referenceDocuments,
    ),
    processingStatus:
      objectRecord.processing_status ?? objectRecord.processingStatus,
  };
}

export function normalizeAutoTagResultDetail(
  payload: unknown,
): AutoTagResultDetail | null {
  const data = extractDetailPayload(payload);
  if (!data) return null;

  const header = parseDetailObject(data);
  const rawTags = data.tags;
  const tags: AutoTagDetailTag[] = [];
  if (Array.isArray(rawTags)) {
    rawTags.forEach((item, index) => {
      const tag = asRecord(item);
      if (!tag) return;
      const concept =
        readString(tag, "concept_display_name", "conceptDisplayName") ||
        readString(tag, "concept");
      const tagValue = readString(
        tag,
        "concept_value_name",
        "conceptValueName",
      );
      const valueCode = readString(
        tag,
        "concept_value_code",
        "conceptValueCode",
      );
      tags.push({
        id: valueCode || `${concept || "tag"}-${index}`,
        concept,
        tagValue,
        origin: normalizeTagOrigin(
          readString(tag, "triggered_by", "triggeredBy", "origin"),
        ),
        analysis: readString(tag, "analysis"),
        summary: readString(tag, "summary"),
        recommendation: readString(tag, "recommendation"),
      });
    });
  }

  return {
    objectId: header.objectId,
    title: header.title,
    version: header.version,
    references: header.references,
    tags,
  };
}

export function normalizeConditionalTagResultDetail(
  payload: unknown,
): ConditionalTagResultDetail | null {
  const data = extractDetailPayload(payload);
  if (!data) return null;

  const header = parseDetailObject(data);
  const rawRules = data.matched_rules ?? data.matchedRules;
  const rules: ConditionalTagDetailRule[] = [];
  if (Array.isArray(rawRules)) {
    rawRules.forEach((item, index) => {
      const rule = asRecord(item);
      if (!rule) return;
      const concept =
        readString(rule, "concept_display_name", "conceptDisplayName") ||
        readString(rule, "concept");
      const tagValue = readString(
        rule,
        "concept_value_name",
        "conceptValueName",
      );
      const ruleId = readString(rule, "rule_id", "ruleId");
      rules.push({
        id: ruleId || `${concept || "rule"}-${index}`,
        concept,
        tagValue,
        ruleId,
        matchedCondition: rule.matched_condition ?? rule.matchedCondition ?? null,
      });
    });
  }

  return {
    objectId: header.objectId,
    title: header.title,
    version: header.version,
    status: normalizeConditionalStatus(header.processingStatus),
    references: header.references,
    rules,
  };
}

const inFlightDetailRequests = new Map<string, Promise<unknown>>();

function detailRequestKey(
  path: string,
  query: TaggingResultDetailRequest,
): string {
  return JSON.stringify({
    path,
    ...buildTaggingResultsDetailBody(query),
  });
}

async function fetchTaggingResultDetail<T>(
  path: string,
  query: TaggingResultDetailRequest,
  parse: (payload: unknown) => T | null,
  fallbackError: string,
): Promise<T> {
  const key = detailRequestKey(path, query);
  const existing = inFlightDetailRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const request = (async () => {
    const result = await apiClient.post<unknown>(
      path,
      buildTaggingResultsDetailBody(query),
    );
    if (result.status !== "success") {
      throw new Error(result.message?.trim() || fallbackError);
    }
    const detail = parse(result);
    if (!detail) {
      throw new Error(fallbackError);
    }
    return detail;
  })().finally(() => {
    inFlightDetailRequests.delete(key);
  });

  inFlightDetailRequests.set(key, request);
  return request;
}

export async function fetchAutoTagResultDetail(
  query: TaggingResultDetailRequest,
): Promise<AutoTagResultDetail> {
  return fetchTaggingResultDetail(
    TAGGING_RESULTS_AUTO_TAG_DETAIL_PATH,
    query,
    normalizeAutoTagResultDetail,
    "Failed to load Classification Result.",
  );
}

export async function fetchConditionalTagResultDetail(
  query: TaggingResultDetailRequest,
): Promise<ConditionalTagResultDetail> {
  return fetchTaggingResultDetail(
    TAGGING_RESULTS_CONDITIONAL_TAG_DETAIL_PATH,
    query,
    normalizeConditionalTagResultDetail,
    "Failed to load Conditional Classification Result.",
  );
}
